import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink, ExternalLinkIcon } from 'lucide-react'
import { useMemo } from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { adminRecordInspector } from 'rails_routes'
import { Badge } from 'slab'
import { useGetAdminEmployersEmployerIdComplianceChecks } from 'utils/web_api'
import { type AdminComplianceCheck } from 'utils/web_api.schemas'

import EmployerComplianceChecksResultBadge from './EmployerComplianceChecksResultBadge'
import EmployerComplianceCheckTypeBadge from './EmployerComplianceCheckTypeBadge'
import EmployerComplianceEntityBadge from './EmployerComplianceEntityBadge'
import OverrideEmployerCheckStatusButton from './OverrideEmployerCheckStatusButton'

interface EmployerComplianceChecksTableProps {
  employerId: string
}

const columnHelper = createColumnHelper<AdminComplianceCheck>()

const createColumns = () => [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: ({ getValue }) => (
      <Badge
        type='info'
        actionIcon={ExternalLink}
        onActionClick={() => {
          window.open(adminRecordInspector.show({ id: getValue() }), '_blank')
        }}
      >
        {getValue().slice(-6)}
      </Badge>
    ),
  }),
  columnHelper.accessor('policy_type', {
    header: 'Check Type',
    cell: ({ row }) => (
      <EmployerComplianceCheckTypeBadge policyType={row.original.policy_type} policyId={row.original.policy_id} />
    ),
  }),
  columnHelper.accessor('entity_id', {
    header: 'Entity',
    cell: ({ row }) => (
      <EmployerComplianceEntityBadge
        entityType={row.original.entity_type}
        entityId={row.original.entity_id}
        entityName={row.original.entity_name}
      />
    ),
  }),
  columnHelper.accessor('state', {
    header: 'State',
    cell: ({ getValue }) => <EmployerComplianceChecksResultBadge state={getValue()} />,
  }),
  columnHelper.accessor('created_at', {
    header: 'Created At',
    cell: ({ getValue }) => {
      const value = getValue()
      if (!value) return '-'
      return new Date(value).toLocaleDateString()
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const { provider, remote_link: remoteLink, state } = row.original

      return (
        <div className='flex item-center justify-start space-x-2 text-base'>
          {remoteLink && (
            <DisplayTooltipOnHover text={`External link to ${provider}`}>
              <ExternalLinkIcon
                size={16}
                strokeWidth={2}
                className='cursor-pointer'
                onClick={() => window.open(remoteLink, '_blank')}
              />
            </DisplayTooltipOnHover>
          )}
          <OverrideEmployerCheckStatusButton
            state={state}
            checkId={row.original.id}
            provider={provider || ''}
          />
        </div>
      )
    }
  })
]

const EmployerComplianceChecksTable = ({ employerId }: EmployerComplianceChecksTableProps) => {
  const { data: complianceChecks, isLoading, error } = useGetAdminEmployersEmployerIdComplianceChecks(
    employerId,
    { query: { enabled: !!employerId } }
  )

  // Memoize compliance checks data to prevent infinite re-renders
  const checks = useMemo(() => {
    return (complianceChecks as AdminComplianceCheck[]) || []
  }, [complianceChecks])

  const columns = useMemo(() => createColumns(), [])

  const table = useReactTable({
    data: checks,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={!!error}
      noDataMessage='No compliance checks found for this employer'
      tableMessageHeightClass='h-14'
      paginationLabelSingular='compliance check'
      paginationLabelPlural='compliance checks'
      loadingSkeletonPageSizeOverride={5}
    />
  )
}

export default EmployerComplianceChecksTable
